import { PackedAppearance } from '@app/modules/persons/appearances/pack-appearances-into-rows';
import { PersonLane } from '@app/modules/persons/appearances/types';
import { TimeRange } from '@mi-front/core';
import { Stream } from '@mi-front/ui';
import { Box, Tooltip } from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { STREAM_UI_CONFIG } from '../../../../../consts';
import { HatchedStrip } from '../../../../../features/live-topics/track/track-items/elements';
import { TRACK_EMPTY_BAND_RADIUS_PX } from '../../../../../stream-track-visual-tokens';
import { HoverLaneSetter } from '../../shared/apply-lane-hover';
import { personsTrackMessages } from '../../shared/persons-track-messages';
import { PersonsCastCluster } from './PersonsCastCluster';
import { PersonsSummaryBar } from './PersonsSummaryBar';

const HEIGHT = STREAM_UI_CONFIG.personsSummaryHeight;

type PersonsSummaryStripProps = {
  /** Focus-clipped lanes for the cast cluster. */
  lanes: PersonLane[];
  /** Live focus range driving the Stream viewport. */
  range: TimeRange<number>;
  /** Stream-range packing (row assignments); filtered to `range` for render. */
  packed: PackedAppearance[];
  /** True when the loaded segment range has no persons (hatched empty strip). */
  showEmptyState: boolean;
  hoveredLaneKey: string | null;
  onHoverLane: HoverLaneSetter;
};

/** Appearances that overlap the visible focus window (row comes from full-range pack). */
function filterPackedToRange(
  packed: PackedAppearance[],
  range: TimeRange<number>,
): PackedAppearance[] {
  return packed.filter((p) => p.appearance.to > range.from && p.appearance.from < range.to);
}

/**
 * Default (compact) Personas track body under the focus area.
 *
 * Shows all face/voice detections packed into a few sub-rows on the live focus
 * timeline, with a left cast cluster of who appears in the current window.
 * When the loaded stream range has no persons at all, shows a hatched empty
 * strip instead (empty focus alone does not).
 */
export function PersonsSummaryStrip({
  lanes,
  range,
  packed,
  showEmptyState,
  hoveredLaneKey,
  onHoverLane,
}: PersonsSummaryStripProps) {
  const { formatMessage } = useIntl();

  const visiblePacked = useMemo(
    () => filterPackedToRange(packed, range),
    [packed, range.from, range.to],
  );

  return (
    <Box data-testid="persons-track" sx={{ position: 'relative', height: HEIGHT }}>
      <Stream
        showAxis={false}
        height={HEIGHT}
        range={range}
        renderHtml={() =>
          showEmptyState ? (
            <Tooltip title={formatMessage(personsTrackMessages.empty)} placement="top">
              <Box
                sx={{
                  position: 'absolute',
                  top: STREAM_UI_CONFIG.trackContentInsetY,
                  left: 0,
                  right: 0,
                  height: HEIGHT - 2 * STREAM_UI_CONFIG.trackContentInsetY,
                  borderRadius: `${TRACK_EMPTY_BAND_RADIUS_PX}px`,
                  overflow: 'hidden',
                }}
              >
                <HatchedStrip />
              </Box>
            </Tooltip>
          ) : (
            <>
              {visiblePacked.map((p) => (
                <PersonsSummaryBar
                  key={`${p.lane.laneKey}-${p.appearance.source}-${p.appearance.from}`}
                  packed={p}
                  dimmed={!!hoveredLaneKey && hoveredLaneKey !== p.lane.laneKey}
                  emphasized={hoveredLaneKey === p.lane.laneKey}
                  onHoverLane={onHoverLane}
                />
              ))}
            </>
          )
        }
      />
      {lanes.length > 0 && (
        <PersonsCastCluster
          lanes={lanes}
          hoveredLaneKey={hoveredLaneKey}
          onHoverLane={onHoverLane}
        />
      )}
    </Box>
  );
}
